import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  query,
  where,
  onSnapshot,
  Timestamp,
  DocumentReference,
  writeBatch,
  increment,
  collectionGroup
} from 'firebase/firestore';
import { db } from '../firebase';
import {
  OrgStats,
  MonthlyDebitDoc,
  MonthlyDebitItem,
  MonthlyCreditDoc,
  MonthlyCreditItem,
  MonthlyExpenseDoc,
  MonthlyExpenseItem,
  Receipt,
  Transaction
} from '../types';

// ======================== HELPERS ========================

/** Build the document ID for a monthly aggregation doc. */
export const getMonthKey = (date: Date, academyId?: string): string => {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  return `${yyyy}-${mm}_${academyId || 'general'}`;
};

/** Build the document ID for an OrgStats doc. */
export const getStatsDocId = (academyId?: string): string => {
  return academyId || 'general';
};

/** Get the first and last moment of a given month as Timestamps. */
export const getMonthBounds = (year: number, month: number): { startDate: Timestamp; endDate: Timestamp } => {
  const startDate = Timestamp.fromDate(new Date(year, month - 1, 1, 0, 0, 0, 0));
  const endDate = Timestamp.fromDate(new Date(year, month, 0, 23, 59, 59, 999));
  return { startDate, endDate };
};

// ======================== STATS CRUD (top 4 cards) ========================

/**
 * Atomically adjust the counters on an OrgStats doc.
 * Uses setDoc with merge + increment so the doc is created if missing.
 */
export const updateOrgStats = async (
  orgId: string,
  academyId: string | undefined,
  delta: { overdue?: number; pending?: number; availableCredit?: number }
): Promise<void> => {
  const docId = getStatsDocId(academyId);
  const ref = doc(db, 'organizations', orgId, 'stats', docId);

  const updates: Record<string, any> = { updatedAt: Timestamp.now() };
  if (delta.overdue !== undefined && delta.overdue !== 0) updates.overdue = increment(delta.overdue);
  if (delta.pending !== undefined && delta.pending !== 0) updates.pending = increment(delta.pending);
  if (delta.availableCredit !== undefined && delta.availableCredit !== 0) updates.availableCredit = increment(delta.availableCredit);

  // Also store academyRef for convenience
  if (academyId) {
    updates.academyRef = doc(db, 'organizations', orgId, 'academies', academyId);
  } else {
    updates.academyRef = null;
  }

  await setDoc(ref, updates, { merge: true });
};

/** Read a single stats doc (for a specific academy) or sum all stats docs. */
export const getOrgStats = async (
  orgId: string,
  academyId?: string
): Promise<OrgStats> => {
  if (academyId) {
    const ref = doc(db, 'organizations', orgId, 'stats', academyId);
    const snap = await getDoc(ref);
    if (snap.exists()) {
      return snap.data() as OrgStats;
    }
    return { overdue: 0, pending: 0, availableCredit: 0, academyRef: null, updatedAt: Timestamp.now() };
  }

  // No academy → sum all stats docs
  const colRef = collection(db, 'organizations', orgId, 'stats');
  const snapshot = await getDocs(colRef);
  const summed: OrgStats = { overdue: 0, pending: 0, availableCredit: 0, academyRef: null, updatedAt: Timestamp.now() };
  snapshot.forEach(d => {
    const data = d.data() as OrgStats;
    summed.overdue += data.overdue || 0;
    summed.pending += data.pending || 0;
    summed.availableCredit += data.availableCredit || 0;
  });
  return summed;
};

/** Real-time listener on stats docs. Returns unsubscribe function. */
export const getOrgStatsRealtime = (
  orgId: string,
  academyId: string | undefined,
  callback: (stats: OrgStats) => void
): (() => void) => {
  if (academyId) {
    const ref = doc(db, 'organizations', orgId, 'stats', academyId);
    return onSnapshot(ref, snap => {
      if (snap.exists()) {
        callback(snap.data() as OrgStats);
      } else {
        callback({ overdue: 0, pending: 0, availableCredit: 0, academyRef: null, updatedAt: Timestamp.now() });
      }
    });
  }

  // Listen to all stats docs
  const colRef = collection(db, 'organizations', orgId, 'stats');
  return onSnapshot(colRef, snapshot => {
    const summed: OrgStats = { overdue: 0, pending: 0, availableCredit: 0, academyRef: null, updatedAt: Timestamp.now() };
    snapshot.forEach(d => {
      const data = d.data() as OrgStats;
      summed.overdue += data.overdue || 0;
      summed.pending += data.pending || 0;
      summed.availableCredit += data.availableCredit || 0;
    });
    callback(summed);
  });
};

// ======================== MONTHLY DEBIT ========================

export const addDebitItem = async (orgId: string, receipt: Receipt): Promise<void> => {
  const invoiceDate = receipt.product?.invoiceDate?.toDate() || receipt.createdAt?.toDate() || new Date();
  const monthKey = getMonthKey(invoiceDate, receipt.academyId);
  const ref = doc(db, 'organizations', orgId, 'debit', monthKey);

  const year = invoiceDate.getFullYear();
  const month = invoiceDate.getMonth() + 1;
  const { startDate, endDate } = getMonthBounds(year, month);

  const item: MonthlyDebitItem = {
    receiptId: receipt.id,
    amount: receipt.amount,
    date: receipt.product?.invoiceDate || receipt.createdAt || Timestamp.now(),
    productRef: receipt.product?.productRef || null,
    productName: receipt.product?.name || 'Unknown',
    userId: receipt.userRef?.id || ''
  };

  // Try to read existing doc and append, or create new
  const snap = await getDoc(ref);
  if (snap.exists()) {
    const data = snap.data() as MonthlyDebitDoc;
    const items = [...data.items, item];
    await updateDoc(ref, { items, updatedAt: Timestamp.now() });
  } else {
    const newDoc: MonthlyDebitDoc = {
      startDate,
      endDate,
      academyRef: receipt.academyId ? doc(db, 'organizations', orgId, 'academies', receipt.academyId) : null,
      items: [item],
      updatedAt: Timestamp.now()
    };
    await setDoc(ref, newDoc);
  }
};

export const removeDebitItem = async (
  orgId: string,
  receiptId: string,
  month: string,
  academyId?: string
): Promise<void> => {
  const monthKey = `${month}_${academyId || 'general'}`;
  const ref = doc(db, 'organizations', orgId, 'debit', monthKey);
  const snap = await getDoc(ref);
  if (!snap.exists()) return;

  const data = snap.data() as MonthlyDebitDoc;
  const items = data.items.filter(i => i.receiptId !== receiptId);
  await updateDoc(ref, { items, updatedAt: Timestamp.now() });
};

export const updateDebitItem = async (
  orgId: string,
  receiptId: string,
  month: string,
  academyId: string | undefined,
  updates: Partial<MonthlyDebitItem>
): Promise<void> => {
  const monthKey = `${month}_${academyId || 'general'}`;
  const ref = doc(db, 'organizations', orgId, 'debit', monthKey);
  const snap = await getDoc(ref);
  if (!snap.exists()) return;

  const data = snap.data() as MonthlyDebitDoc;
  const items = data.items.map(i =>
    i.receiptId === receiptId ? { ...i, ...updates } : i
  );
  await updateDoc(ref, { items, updatedAt: Timestamp.now() });
};

/** Get monthly debit data. If academyId is undefined, merge all academy docs for that month. */
export const getMonthlyDebit = async (
  orgId: string,
  month: string, // "YYYY-MM"
  academyId?: string
): Promise<MonthlyDebitDoc | null> => {
  if (academyId) {
    const ref = doc(db, 'organizations', orgId, 'debit', `${month}_${academyId}`);
    const snap = await getDoc(ref);
    return snap.exists() ? (snap.data() as MonthlyDebitDoc) : null;
  }

  // Merge all academy docs for the month
  const colRef = collection(db, 'organizations', orgId, 'debit');
  const snapshot = await getDocs(colRef);
  let merged: MonthlyDebitDoc | null = null;

  snapshot.forEach(d => {
    if (d.id.startsWith(month + '_')) {
      const data = d.data() as MonthlyDebitDoc;
      if (!merged) {
        merged = { ...data, items: [...data.items] };
      } else {
        merged.items.push(...data.items);
      }
    }
  });

  return merged;
};

// ======================== MONTHLY CREDIT ========================

export const addCreditItem = async (
  orgId: string,
  receipt: Receipt,
  paymentMethod: string = 'Unknown'
): Promise<void> => {
  const paymentDate = receipt.paymentDate?.toDate() || receipt.createdAt?.toDate() || new Date();
  const monthKey = getMonthKey(paymentDate, receipt.academyId);
  const ref = doc(db, 'organizations', orgId, 'credit', monthKey);

  const year = paymentDate.getFullYear();
  const month = paymentDate.getMonth() + 1;
  const { startDate, endDate } = getMonthBounds(year, month);

  const item: MonthlyCreditItem = {
    receiptId: receipt.id,
    amount: receipt.amount,
    date: receipt.paymentDate || receipt.createdAt || Timestamp.now(),
    paymentMethod,
    userId: receipt.userRef?.id || ''
  };

  const snap = await getDoc(ref);
  if (snap.exists()) {
    const data = snap.data() as MonthlyCreditDoc;
    const items = [...data.items, item];
    await updateDoc(ref, { items, updatedAt: Timestamp.now() });
  } else {
    const newDoc: MonthlyCreditDoc = {
      startDate,
      endDate,
      academyRef: receipt.academyId ? doc(db, 'organizations', orgId, 'academies', receipt.academyId) : null,
      items: [item],
      updatedAt: Timestamp.now()
    };
    await setDoc(ref, newDoc);
  }
};

export const removeCreditItem = async (
  orgId: string,
  receiptId: string,
  month: string,
  academyId?: string
): Promise<void> => {
  const monthKey = `${month}_${academyId || 'general'}`;
  const ref = doc(db, 'organizations', orgId, 'credit', monthKey);
  const snap = await getDoc(ref);
  if (!snap.exists()) return;

  const data = snap.data() as MonthlyCreditDoc;
  const items = data.items.filter(i => i.receiptId !== receiptId);
  await updateDoc(ref, { items, updatedAt: Timestamp.now() });
};

export const getMonthlyCredit = async (
  orgId: string,
  month: string,
  academyId?: string
): Promise<MonthlyCreditDoc | null> => {
  if (academyId) {
    const ref = doc(db, 'organizations', orgId, 'credit', `${month}_${academyId}`);
    const snap = await getDoc(ref);
    return snap.exists() ? (snap.data() as MonthlyCreditDoc) : null;
  }

  const colRef = collection(db, 'organizations', orgId, 'credit');
  const snapshot = await getDocs(colRef);
  let merged: MonthlyCreditDoc | null = null;

  snapshot.forEach(d => {
    if (d.id.startsWith(month + '_')) {
      const data = d.data() as MonthlyCreditDoc;
      if (!merged) {
        merged = { ...data, items: [...data.items] };
      } else {
        merged.items.push(...data.items);
      }
    }
  });

  return merged;
};

// ======================== MONTHLY EXPENSES ========================

export const addExpenseItem = async (orgId: string, transaction: Transaction): Promise<void> => {
  const txDate = transaction.date?.toDate() || transaction.createdAt?.toDate() || new Date();
  const monthKey = getMonthKey(txDate, transaction.academyId);
  const ref = doc(db, 'organizations', orgId, 'expenses', monthKey);

  const year = txDate.getFullYear();
  const month = txDate.getMonth() + 1;
  const { startDate, endDate } = getMonthBounds(year, month);

  const item: MonthlyExpenseItem = {
    transactionId: transaction.id,
    amount: Math.abs(transaction.amount),
    date: transaction.date || transaction.createdAt || Timestamp.now(),
    description: transaction.description || ''
  };

  const snap = await getDoc(ref);
  if (snap.exists()) {
    const data = snap.data() as MonthlyExpenseDoc;
    const items = [...data.items, item];
    await updateDoc(ref, { items, updatedAt: Timestamp.now() });
  } else {
    const newDoc: MonthlyExpenseDoc = {
      startDate,
      endDate,
      academyRef: transaction.academyId ? doc(db, 'organizations', orgId, 'academies', transaction.academyId) : null,
      items: [item],
      updatedAt: Timestamp.now()
    };
    await setDoc(ref, newDoc);
  }
};

export const removeExpenseItem = async (
  orgId: string,
  transactionId: string,
  month: string,
  academyId?: string
): Promise<void> => {
  const monthKey = `${month}_${academyId || 'general'}`;
  const ref = doc(db, 'organizations', orgId, 'expenses', monthKey);
  const snap = await getDoc(ref);
  if (!snap.exists()) return;

  const data = snap.data() as MonthlyExpenseDoc;
  const items = data.items.filter(i => i.transactionId !== transactionId);
  await updateDoc(ref, { items, updatedAt: Timestamp.now() });
};

export const getMonthlyExpenses = async (
  orgId: string,
  month: string,
  academyId?: string
): Promise<MonthlyExpenseDoc | null> => {
  if (academyId) {
    const ref = doc(db, 'organizations', orgId, 'expenses', `${month}_${academyId}`);
    const snap = await getDoc(ref);
    return snap.exists() ? (snap.data() as MonthlyExpenseDoc) : null;
  }

  const colRef = collection(db, 'organizations', orgId, 'expenses');
  const snapshot = await getDocs(colRef);
  let merged: MonthlyExpenseDoc | null = null;

  snapshot.forEach(d => {
    if (d.id.startsWith(month + '_')) {
      const data = d.data() as MonthlyExpenseDoc;
      if (!merged) {
        merged = { ...data, items: [...data.items] };
      } else {
        merged.items.push(...data.items);
      }
    }
  });

  return merged;
};

// ======================== BOOTSTRAP / RECALCULATE ========================

/**
 * Rebuild ALL stats / debit / credit / expense docs from scratch
 * by querying raw receipts & transactions. Uses batched writes.
 */
export const recalculateAllStats = async (orgId: string): Promise<void> => {
  console.log('📊 recalculateAllStats: starting full rebuild for org', orgId);

  // 1. Wipe existing stats/debit/credit/expenses docs
  const subcollections = ['stats', 'debit', 'credit', 'expenses'] as const;
  for (const sub of subcollections) {
    const colRef = collection(db, 'organizations', orgId, sub);
    const snap = await getDocs(colRef);
    const batch = writeBatch(db);
    snap.docs.forEach(d => batch.delete(d.ref));
    if (snap.docs.length > 0) await batch.commit();
  }

  // 1b. Build userId → academyId map from player records for academy fallback
  const playersQuery = query(
    collection(db, 'players'),
    where('organizationId', '==', orgId)
  );
  const playersSnap = await getDocs(playersQuery);
  const userAcademyMap = new Map<string, string>();
  playersSnap.forEach(d => {
    const player = d.data();
    const userId = player.userId as string;
    const academyIds = player.academyId as string[];
    if (userId && academyIds && academyIds.length > 0 && !userAcademyMap.has(userId)) {
      userAcademyMap.set(userId, academyIds[0]);
    }
  });
  console.log(`📊 recalculateAllStats: Built user→academy map: ${userAcademyMap.size} users`);

  // 2. Query all receipts for org
  const receiptsQuery = query(
    collectionGroup(db, 'receipts'),
    where('organizationId', '==', orgId)
  );
  const receiptsSnap = await getDocs(receiptsQuery);

  // Accumulators
  const statsMap = new Map<string, { overdue: number; pending: number; availableCredit: number }>();
  const debitDocsMap = new Map<string, { startDate: Timestamp; endDate: Timestamp; academyRef: DocumentReference | null; items: MonthlyDebitItem[] }>();
  const creditDocsMap = new Map<string, { startDate: Timestamp; endDate: Timestamp; academyRef: DocumentReference | null; items: MonthlyCreditItem[] }>();

  // Group receipts by user for proper per-user available credit computation
  type ReceiptWithId = Receipt & { _docId: string; _userId: string; _correctedFinancialStatus?: string };
  const userDebits = new Map<string, ReceiptWithId[]>();
  const userCredits = new Map<string, ReceiptWithId[]>();

  // Batch to fix stale financialStatus on debit receipts
  const staleFixBatch = writeBatch(db);
  let staleFixCount = 0;

  // First pass – group receipts by user and build monthly docs + overdue/pending stats
  receiptsSnap.forEach(docSnap => {
    const r = docSnap.data() as Receipt;
    if (r.status === 'deleted') return;

    const userId = docSnap.ref.parent.parent?.id || '';
    const receiptWithId: ReceiptWithId = { ...r, id: r.id || docSnap.id, _docId: docSnap.id, _userId: userId };

    const academyId = r.academyId || userAcademyMap.get(userId);
    receiptWithId.academyId = academyId;

    if (r.type === 'debit') {
      // Group by user for available credit computation
      if (!userDebits.has(userId)) userDebits.set(userId, []);
      userDebits.get(userId)!.push(receiptWithId);

      // Derive the correct financialStatus from receipt state
      let correctedFinancialStatus = r.financialStatus;
      if (r.status === 'completed') {
        correctedFinancialStatus = 'settled';
      } else {
        // For 'active', 'paid', or undefined status — check deadline for overdue/pending
        const now = new Date();
        const deadline = r.product?.deadline?.toDate?.();
        correctedFinancialStatus = (deadline && deadline < now) ? 'overdue' : 'pending';
      }

      // Store corrected status for the remaining-balance pass
      receiptWithId._correctedFinancialStatus = correctedFinancialStatus;

      // Build Firestore fixes for this receipt (status + financialStatus + academyId)
      const receiptFixes: Record<string, any> = {};
      // Fix undefined/missing status → 'active'
      if (!r.status) {
        receiptFixes.status = 'active';
      }
      if (correctedFinancialStatus !== r.financialStatus) {
        receiptFixes.financialStatus = correctedFinancialStatus;
      }
      if (!r.academyId && academyId) {
        receiptFixes.academyId = academyId;
      }
      if (Object.keys(receiptFixes).length > 0) {
        console.log(`📊 FIX debit receipt: receiptId=${docSnap.id}, fixes=${JSON.stringify(receiptFixes)}`);
        staleFixBatch.update(docSnap.ref, receiptFixes);
        staleFixCount++;
      }

      // NOTE: overdue/pending stats are computed in a separate pass using remaining balances

      // Monthly debit item
      const invoiceDate = r.product?.invoiceDate?.toDate() || r.createdAt?.toDate() || new Date();
      const monthKey = getMonthKey(invoiceDate, academyId);

      if (!debitDocsMap.has(monthKey)) {
        const year = invoiceDate.getFullYear();
        const month = invoiceDate.getMonth() + 1;
        const { startDate, endDate } = getMonthBounds(year, month);
        debitDocsMap.set(monthKey, {
          startDate,
          endDate,
          academyRef: academyId ? doc(db, 'organizations', orgId, 'academies', academyId) : null,
          items: []
        });
      }
      debitDocsMap.get(monthKey)!.items.push({
        receiptId: r.id || docSnap.id,
        amount: r.amount,
        date: r.product?.invoiceDate || r.createdAt || Timestamp.now(),
        productRef: r.product?.productRef || null,
        productName: r.product?.name || 'Unknown',
        userId
      });
    }

    if (r.type === 'credit') {
      // Group by user for available credit computation
      if (!userCredits.has(userId)) userCredits.set(userId, []);
      userCredits.get(userId)!.push(receiptWithId);

      // Fix credit receipt data issues
      const creditFixes: Record<string, any> = {};
      if (!r.academyId && academyId) {
        creditFixes.academyId = academyId;
      }
      // Credits linked to debits should be 'completed', not 'active'
      const hasLinks = r.siblingReceiptRefs && r.siblingReceiptRefs.length > 0;
      if (hasLinks && r.status === 'active') {
        creditFixes.status = 'completed';
      }
      if (!r.status) {
        creditFixes.status = hasLinks ? 'completed' : 'active';
      }
      if (Object.keys(creditFixes).length > 0) {
        console.log(`📊 FIX credit receipt: receiptId=${docSnap.id}, fixes=${JSON.stringify(creditFixes)}`);
        staleFixBatch.update(docSnap.ref, creditFixes);
        staleFixCount++;
      }

      // Monthly credit item
      const paymentDate = r.paymentDate?.toDate() || r.createdAt?.toDate() || new Date();
      const monthKey = getMonthKey(paymentDate, academyId);

      if (!creditDocsMap.has(monthKey)) {
        const year = paymentDate.getFullYear();
        const month = paymentDate.getMonth() + 1;
        const { startDate, endDate } = getMonthBounds(year, month);
        creditDocsMap.set(monthKey, {
          startDate,
          endDate,
          academyRef: academyId ? doc(db, 'organizations', orgId, 'academies', academyId) : null,
          items: []
        });
      }
      creditDocsMap.get(monthKey)!.items.push({
        receiptId: r.id || docSnap.id,
        amount: r.amount,
        date: r.paymentDate || r.createdAt || Timestamp.now(),
        paymentMethod: 'Cash', // Default for migrated data
        userId
      });
    }
  });

  // Commit stale financialStatus fixes
  if (staleFixCount > 0) {
    await staleFixBatch.commit();
    console.log(`📊 recalculateAllStats: Fixed ${staleFixCount} stale financialStatus fields`);
  }

  // Second pass – compute overdue/pending using remaining balances (matches Players page logic)
  userDebits.forEach((debits, userId) => {
    const credits = userCredits.get(userId) || [];

    for (const debit of debits) {
      if (debit._correctedFinancialStatus === 'settled') {
        console.log(`📊 SKIPPED debit (settled): receiptId=${debit._docId}, userId=${userId}, amount=${debit.amount}`);
        continue;
      }

      // Find credits linked to this debit (same logic as calculateUserOutstandingBalance)
      const linkedCredits = credits.filter(c =>
        c.siblingReceiptRefs &&
        c.siblingReceiptRefs.some((ref: any) => ref.id === debit._docId || ref.id === debit.id)
      );

      const totalCreditsApplied = linkedCredits.reduce((sum, c) => sum + c.amount, 0);
      const remainingBalance = Math.max(0, debit.amount - totalCreditsApplied);

      if (remainingBalance <= 0) {
        console.log(`📊 SKIPPED debit (fully paid by credits): receiptId=${debit._docId}, userId=${userId}, amount=${debit.amount}, creditsApplied=${totalCreditsApplied}`);
        continue;
      }

      const debitAcademyId = debit.academyId;
      const statsKey = getStatsDocId(debitAcademyId);
      if (!statsMap.has(statsKey)) {
        statsMap.set(statsKey, { overdue: 0, pending: 0, availableCredit: 0 });
      }
      const stats = statsMap.get(statsKey)!;

      if (debit._correctedFinancialStatus === 'overdue') {
        stats.overdue += remainingBalance;
        console.log(`📊 OVERDUE debit: receiptId=${debit._docId}, userId=${userId}, fullAmount=${debit.amount}, creditsApplied=${totalCreditsApplied}, remaining=${remainingBalance}, academy=${debitAcademyId}`);
      } else if (debit._correctedFinancialStatus === 'pending') {
        stats.pending += remainingBalance;
        console.log(`📊 PENDING debit: receiptId=${debit._docId}, userId=${userId}, fullAmount=${debit.amount}, creditsApplied=${totalCreditsApplied}, remaining=${remainingBalance}, academy=${debitAcademyId}`);
      }
    }
  });

  // Third pass – compute available credit per user as NET positive balance
  // (availableCredits - outstandingDebits per user), matching the player list display.
  // Credits linked only to deleted/missing debits are also counted as available.
  Array.from(userCredits.entries()).forEach(([userId, credits]) => {
    const activeDebits = userDebits.get(userId) || [];

    // Compute total unlinked/available credits for this user (mirrors calculateUserOutstandingBalance)
    let totalAvailableCredits = 0;
    for (const credit of credits) {
      if (!credit.siblingReceiptRefs || credit.siblingReceiptRefs.length === 0) {
        totalAvailableCredits += credit.amount;
      } else {
        // Credit is available if all its sibling debits are deleted/missing from active debits
        const allSiblingsDeleted = (credit.siblingReceiptRefs as any[]).every((ref: any) => {
          const refId = ref.id;
          return !activeDebits.some(d => d._docId === refId || d.id === refId);
        });
        if (allSiblingsDeleted) {
          totalAvailableCredits += credit.amount;
        }
      }
    }

    // Compute total remaining debts for this user (same logic as second pass)
    let totalRemainingDebts = 0;
    for (const debit of activeDebits) {
      if (debit._correctedFinancialStatus === 'settled') continue;
      const linkedCredits = credits.filter((c: ReceiptWithId) =>
        c.siblingReceiptRefs &&
        (c.siblingReceiptRefs as any[]).some((ref: any) => ref.id === debit._docId || ref.id === debit.id)
      );
      const creditsApplied = linkedCredits.reduce((sum: number, c: ReceiptWithId) => sum + c.amount, 0);
      totalRemainingDebts += Math.max(0, debit.amount - creditsApplied);
    }

    // Only count the NET positive balance (matching player list: max(0, credits - debts))
    const netCredit = Math.max(0, totalAvailableCredits - totalRemainingDebts);
    if (netCredit > 0) {
      const primaryAcademyId = userAcademyMap.get(userId);
      const statsKey = getStatsDocId(primaryAcademyId);
      if (!statsMap.has(statsKey)) {
        statsMap.set(statsKey, { overdue: 0, pending: 0, availableCredit: 0 });
      }
      statsMap.get(statsKey)!.availableCredit += netCredit;
      console.log(`📊 NET CREDIT: userId=${userId}, availableCredits=${totalAvailableCredits}, remainingDebts=${totalRemainingDebts}, netCredit=${netCredit}`);
    }
  });

  // 3. Query all expense transactions
  const txQuery = query(
    collection(db, 'transactions'),
    where('organizationId', '==', orgId),
    where('type', '==', 'expense')
  );
  const txSnap = await getDocs(txQuery);
  const expenseDocsMap = new Map<string, { startDate: Timestamp; endDate: Timestamp; academyRef: DocumentReference | null; items: MonthlyExpenseItem[] }>();

  txSnap.forEach(docSnap => {
    const tx = docSnap.data() as Transaction;
    if (tx.isDeleted) return;

    const txDate = tx.date?.toDate() || tx.createdAt?.toDate() || new Date();
    const monthKey = getMonthKey(txDate, tx.academyId);

    if (!expenseDocsMap.has(monthKey)) {
      const year = txDate.getFullYear();
      const month = txDate.getMonth() + 1;
      const { startDate, endDate } = getMonthBounds(year, month);
      expenseDocsMap.set(monthKey, {
        startDate,
        endDate,
        academyRef: tx.academyId ? doc(db, 'organizations', orgId, 'academies', tx.academyId) : null,
        items: []
      });
    }
    expenseDocsMap.get(monthKey)!.items.push({
      transactionId: tx.id || docSnap.id,
      amount: Math.abs(tx.amount),
      date: tx.date || tx.createdAt || Timestamp.now(),
      description: tx.description || ''
    });
  });

  // 4. Write everything in batches
  const batch = writeBatch(db);

  // Stats docs
  statsMap.forEach((stats, docId) => {
    const ref = doc(db, 'organizations', orgId, 'stats', docId);
    const data: OrgStats = {
      ...stats,
      academyRef: docId === 'general' ? null : doc(db, 'organizations', orgId, 'academies', docId),
      updatedAt: Timestamp.now()
    };
    batch.set(ref, data);
  });

  // Debit docs
  debitDocsMap.forEach((data, monthKey) => {
    const ref = doc(db, 'organizations', orgId, 'debit', monthKey);
    batch.set(ref, { ...data, updatedAt: Timestamp.now() });
  });

  // Credit docs
  creditDocsMap.forEach((data, monthKey) => {
    const ref = doc(db, 'organizations', orgId, 'credit', monthKey);
    batch.set(ref, { ...data, updatedAt: Timestamp.now() });
  });

  // Expense docs
  expenseDocsMap.forEach((data, monthKey) => {
    const ref = doc(db, 'organizations', orgId, 'expenses', monthKey);
    batch.set(ref, { ...data, updatedAt: Timestamp.now() });
  });

  await batch.commit();

  // Log final stats summary
  statsMap.forEach((stats, key) => {
    console.log(`📊 FINAL STATS [${key}]: overdue=${stats.overdue}, pending=${stats.pending}, availableCredit=${stats.availableCredit}`);
  });

  // Diagnostic: per-user receipt audit
  const allUserIds = new Set(Array.from(userDebits.keys()).concat(Array.from(userCredits.keys())));
  allUserIds.forEach(userId => {
    const debits = userDebits.get(userId) || [];
    const credits = userCredits.get(userId) || [];

    console.log(`\n🔍 AUDIT user=${userId}`);
    console.log(`   Debits (${debits.length}):`);
    debits.forEach(d => {
      const linkedCreditIds = (d.siblingReceiptRefs || []).map((ref: any) => ref.id);
      const linkedCredits = credits.filter(c =>
        c.siblingReceiptRefs &&
        c.siblingReceiptRefs.some((ref: any) => ref.id === d._docId || ref.id === d.id)
      );
      const creditsApplied = linkedCredits.reduce((sum, c) => sum + c.amount, 0);
      console.log(`     debitId=${d._docId}, amount=${d.amount}, status=${d.status}, financialStatus=${d._correctedFinancialStatus}, academyId=${d.academyId}, deadline=${d.product?.deadline?.toDate?.()}, linkedCredits=${linkedCreditIds.join(',') || 'none'}, creditsAppliedFromCreditSide=${creditsApplied}, remaining=${Math.max(0, d.amount - creditsApplied)}`);
    });

    console.log(`   Credits (${credits.length}):`);
    credits.forEach(c => {
      const linkedDebitIds = (c.siblingReceiptRefs || []).map((ref: any) => ref.id);
      console.log(`     creditId=${c._docId}, amount=${c.amount}, status=${c.status}, academyId=${c.academyId}, linkedDebits=${linkedDebitIds.join(',') || 'none'}`);
    });

    const totalDebit = debits.reduce((sum, d) => sum + d.amount, 0);
    const totalCredit = credits.reduce((sum, c) => sum + c.amount, 0);
    const settledDebits = debits.filter(d => d._correctedFinancialStatus === 'settled').reduce((sum, d) => sum + d.amount, 0);
    console.log(`   TOTALS: debits=${totalDebit}, credits=${totalCredit}, settledDebits=${settledDebits}, net=${totalDebit - totalCredit}`);
  });

  console.log('📊 recalculateAllStats: complete', {
    statsDocs: statsMap.size,
    debitDocs: debitDocsMap.size,
    creditDocs: creditDocsMap.size,
    expenseDocs: expenseDocsMap.size
  });
};

// ======================== MIGRATION: Fix missing academyId ========================

/**
 * Finds all receipts and transactions missing `academyId` for an organization,
 * looks up each user's player document to determine their academy,
 * and patches the missing field. After patching, runs recalculateAllStats.
 *
 * Returns a summary of what was fixed.
 */
export const migrateAcademyIds = async (
  orgId: string,
  onProgress?: (message: string) => void
): Promise<{
  receiptsFixed: number;
  transactionsFixed: number;
  skipped: number;
  errors: string[];
}> => {
  const log = (msg: string) => {
    console.log(msg);
    onProgress?.(msg);
  };

  const result = { receiptsFixed: 0, transactionsFixed: 0, skipped: 0, errors: [] as string[] };

  try {
    log('🔄 migrateAcademyIds: Starting migration for org ' + orgId);

    // 1. Load all players for the org to build userId → academyId map
    const playersQuery = query(
      collection(db, 'players'),
      where('organizationId', '==', orgId)
    );
    const playersSnap = await getDocs(playersQuery);

    // Map: userId → first academyId (players can belong to multiple, pick first)
    const userAcademyMap = new Map<string, string>();
    playersSnap.forEach(d => {
      const player = d.data();
      const userId = player.userId as string;
      const academyIds = player.academyId as string[];
      if (userId && academyIds && academyIds.length > 0 && !userAcademyMap.has(userId)) {
        userAcademyMap.set(userId, academyIds[0]);
      }
    });

    log(`👥 Built user→academy map: ${userAcademyMap.size} users mapped`);

    // 2. Fix receipts missing academyId
    const receiptsQuery = query(
      collectionGroup(db, 'receipts'),
      where('organizationId', '==', orgId)
    );
    const receiptsSnap = await getDocs(receiptsQuery);

    log(`📄 Found ${receiptsSnap.size} total receipts, checking for missing academyId...`);

    // Batch writes (Firestore limit: 500 per batch)
    let batchOps: { ref: any; data: any }[] = [];

    const flushBatch = async () => {
      if (batchOps.length === 0) return;
      const b = writeBatch(db);
      batchOps.forEach(op => b.update(op.ref, op.data));
      await b.commit();
      batchOps = [];
    };

    for (const docSnap of receiptsSnap.docs) {
      const receipt = docSnap.data();

      // Get userId from the receipt's parent path: users/{userId}/receipts/{receiptId}
      const userId = docSnap.ref.parent.parent?.id;
      if (!userId) {
        result.skipped++;
        continue;
      }

      const academyId = userAcademyMap.get(userId);
      if (!academyId) {
        if (!receipt.academyId) {
          log(`⚠️ No academy found for user ${userId}, skipping receipt ${docSnap.id}`);
        }
        result.skipped++;
        continue;
      }

      // Skip if already has the correct academyId
      if (receipt.academyId === academyId) continue;

      batchOps.push({
        ref: docSnap.ref,
        data: { academyId, updatedAt: Timestamp.now() }
      });
      result.receiptsFixed++;

      if (batchOps.length >= 400) {
        await flushBatch();
        log(`💾 Flushed batch... ${result.receiptsFixed} receipts fixed so far`);
      }
    }

    await flushBatch();
    log(`✅ Receipts fixed: ${result.receiptsFixed}, skipped: ${result.skipped}`);

    // 3. Fix transactions missing academyId
    const txQuery = query(
      collection(db, 'transactions'),
      where('organizationId', '==', orgId)
    );
    const txSnap = await getDocs(txQuery);

    log(`📄 Found ${txSnap.size} total transactions, checking for missing academyId...`);

    for (const docSnap of txSnap.docs) {
      const tx = docSnap.data();

      // Try to get academyId from the transaction owner's userId
      const ownerId = tx.transactionOwner?.userRef?.id;
      if (!ownerId) {
        result.skipped++;
        continue;
      }

      const academyId = userAcademyMap.get(ownerId);
      if (!academyId) {
        if (!tx.academyId) {
          log(`⚠️ No academy found for tx owner ${ownerId}, skipping transaction ${docSnap.id}`);
        }
        result.skipped++;
        continue;
      }

      // Skip if already has the correct academyId
      if (tx.academyId === academyId) continue;

      batchOps.push({
        ref: docSnap.ref,
        data: { academyId, updatedAt: Timestamp.now() }
      });
      result.transactionsFixed++;

      if (batchOps.length >= 400) {
        await flushBatch();
        log(`💾 Flushed batch... ${result.transactionsFixed} transactions fixed so far`);
      }
    }

    await flushBatch();
    log(`✅ Transactions fixed: ${result.transactionsFixed}`);

    // 4. Rebuild stats with the corrected data
    log('📊 Rebuilding stats with corrected academyId data...');
    await recalculateAllStats(orgId);

    log(`🎉 Migration complete! Receipts: ${result.receiptsFixed}, Transactions: ${result.transactionsFixed}, Skipped: ${result.skipped}`);
    return result;

  } catch (error: any) {
    const msg = `Fatal error: ${error.message}`;
    log(`❌ ${msg}`);
    result.errors.push(msg);
    return result;
  }
};
